import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useRegisterMutation } from '../../app/services/machines';
import { fetchMachines } from '../../features/machines/machinesSlice';
import { fetchMachinesWithDependencies } from '../../helpers/fetchData';
import { useGroups } from '../../hooks/useGroups';
import { useLocations } from '../../hooks/useLocations';
import { useUsers } from '../../hooks/useUsers';
import { MODAL_STYLES } from '../../utils/modalStyles';
import MachineProfileInputs from '../MachineProfileInputs';
import ModalComponent from '../ModalComponent';
import ConfirmDiscardDialog from '../ConfirmDiscardDialog';
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning';

import { DialogContent, DialogTitle, Typography } from '@mui/material';
import {
  ModalDialogActions,
  ModalButton,
  ModalCloseButton,
  ModalAddButton,
} from '../ModalComponents';

import styles from '../ModalComponent/modalStyles.module.css';

export default function ModalAddMachine() {
  const initialForm = {
    tag: '1',
    number: '',
    name: '',
    description: '',
    serialNumber: '',
    responsiblePerson: { id: '' },
    deputy: { id: '' },
    yearOfManufacture: Number(),
    location: { id: '' },
    machineGroup: { id: '' },
    status: 'active',
    isArchived: 'non-archived',
  };

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [errorResponse, setErrorResponse] = useState('');
  const [registerMachine, { isLoading, error }] = useRegisterMutation();

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isInputsDisabled = true;

  // Track data loading states
  const { isLoading: groupsLoading } = useGroups();
  const { isLoading: locationsLoading } = useLocations();
  const { isLoading: usersLoading } = useUsers();

  // Calculate if all required data is loaded
  const isDataLoaded = !groupsLoading && !locationsLoading && !usersLoading;

  const isDirty = JSON.stringify(form) !== JSON.stringify(initialForm);
  const { guardClose, confirmProps } = useUnsavedChangesWarning(isDirty, open);

  const handleOpen = () => {
    // Load dependencies only when modal is opened
    fetchMachinesWithDependencies(dispatch);
    setOpen(true);
  };
  const doClose = () => {
    setOpen(false);
    setForm(initialForm);
  };
  const handleClose = () => guardClose(doClose);

  useEffect(() => {
    if (error) {
      console.log(error);
      setErrorResponse(error.data.message);
    }
  }, [error]);

  const [wasSuccessful, setWasSuccessful] = useState(false);

  useEffect(() => {
    if (wasSuccessful && !isLoading && !error) {
      doClose();
      setErrorResponse('');
      dispatch(fetchMachines());
      setWasSuccessful(false);
    }
  }, [wasSuccessful, isLoading, error, dispatch]);

  const handleSubmit = async () => {
    try {
      const obj = {
        ...form,
        activatedAt: form.activated_at ? form.activated_at : dayjs(new Date()).format('YYYY-MM-DD'),
      };
      await registerMachine(obj);
      setWasSuccessful(true);
    } catch (err) {
      console.error('Error registering machine:', err);
    }
  };

  return (
    <div>
      <ModalAddButton onClick={handleOpen} />
      <ModalComponent open={open} handleClose={handleClose}>
        <div className={styles.modal}>
          <DialogTitle sx={MODAL_STYLES.dialogTitle}>{t('modals.createMachine')}</DialogTitle>
          <ModalCloseButton onClick={handleClose} />
          <DialogContent dividers sx={MODAL_STYLES.dialogContentWithScroll}>
            <div style={MODAL_STYLES.formContainer}>
              <MachineProfileInputs
                form={form}
                setForm={setForm}
                isInputsDisabled={isInputsDisabled}
                isDataLoaded={isDataLoaded}
              />
              {errorResponse &&
                (Array.isArray(errorResponse) ? (
                  errorResponse.map((item, index) => (
                    <Typography key={index} color="error">
                      {item}
                    </Typography>
                  ))
                ) : (
                  <Typography color="error">{errorResponse}</Typography>
                ))}
            </div>
          </DialogContent>
          <ModalDialogActions>
            <ModalButton variant="outlined" color="primary" onClick={handleClose}>
              {t('modals.cancel')}
            </ModalButton>
            <ModalButton variant="contained" color="primary" onClick={handleSubmit}>
              {t('modals.create')}
            </ModalButton>
          </ModalDialogActions>
        </div>
      </ModalComponent>
      <ConfirmDiscardDialog {...confirmProps} />
    </div>
  );
}
